import { HDInsightOnDemandLinkedService } from 'app/model/linkedservice/hdinsightondemand.linkedservice';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { format, formatStringForAppType } from 'app/shared/utils/string.util';
import { supportAzureBlobType } from 'app/registries/linked-service/azure-storage-shared-fields';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { CredentialTypeOptions, HDInsightOndemandClusterResourceGroupAuthTypes } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

const resourceGroupSelection: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    description: stringResources.HdiOnDemand_ToolTip_clusterResourceGroup,
    label: stringResources.ModelName_HdiOnDemand_ResourceGroupSelectionMethod,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{clusterResourceGroup != null ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'resource-group-select',
                    bind: {
                        subscriptionId: 'hostSubscriptionId',
                        resourceGroup: 'clusterResourceGroup'
                    },
                    validators: {
                        subscriptionId: [{ type: 'required', errorMessage: '' }],
                        resourceGroup: [{ type: 'required', errorMessage: '' }]
                    },
                    required: true,
                    isLabelVertical: true
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'textbox',
                    bind: 'hostSubscriptionId',
                    label: stringResources.SubscriptionId,
                    validators: [{ type: 'required', errorMessage: '' }],
                    required: true,
                    isLabelVertical: true
                },
                {
                    type: 'textbox',
                    bind: 'clusterResourceGroup',
                    label: stringResources.ModelName_ResourceGroupName,
                    validators: [{ type: 'required', errorMessage: '' }],
                    required: true,
                    isLabelVertical: true
                }
            ]
        }
    ]
};

const servicePrincipalIdTextBox: ControlMetadata = {
    type: 'textbox',
    bind: 'servicePrincipalId',
    label: stringResources.ModelName_ServicePrincipalID,
    description: stringResources.HdiOnDemand_ToolTip_servicePrincipalId,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

const servicePrincipalKey: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'password',
        label: stringResources.ModelName_ServicePrincipalKey,
        description: stringResources.HdiOnDemand_ToolTip_servicePrincipalKey,
        required: true,
        isLabelVertical: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template, stringResources.HdiOnDemand_FullName,
        stringResources.ModelName_ServicePrincipalKey)
};

const tenantTextbox: ControlMetadata = {
    type: 'tenant-textbox',
    bind: 'tenant',
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    label: stringResources.ModelName_Tenant,
    required: true
};

const resourceGroupAuthOptionDropdown: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: HDInsightOndemandClusterResourceGroupAuthTypes.MSIAuthentication
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: HDInsightOndemandClusterResourceGroupAuthTypes.UserAssignedManagedIdentity
    },
    {
        label: stringResources.ModelName_ServicePrincipalKey,
        value: HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey
    }
];

const managedIdentityServicePrincipalDropdown: ControlMetadata = {
    type: 'select-switch',
    bind: 'clusterResourceGroupAuthType',
    label: stringResources.ModelName_ResourceGroupAuth,
    options: resourceGroupAuthOptionDropdown,
    isLabelVertical: true,
    defaultValue: '',
    required: true,
    description: stringResources.HdiOnDemand_ToolTip_ClusterResourceGroupAuthType,
    cases: [
            {
                caseValue: HDInsightOndemandClusterResourceGroupAuthTypes.UserAssignedManagedIdentity,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            },
            {
                caseValue: HDInsightOndemandClusterResourceGroupAuthTypes.MSIAuthentication,
                children: [
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.ResourceGroup,
                        link: 'https://learn.microsoft.com/azure/data-factory/compute-linked-services#azure-hdinsight-on-demand-linked-service'
                    }
                ]
            },
            {
                caseValue: HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey,
                children: [
                    tenantTextbox,
                    servicePrincipalIdTextBox,
                    servicePrincipalKey
                ]
            }
        ],
        validators: [
            { type: 'required', errorMessage: '' }
        ]
}

export const hdInsightOnDemandMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(HDInsightOnDemandLinkedService).name,
    displayName: __IS_FABRIC_DPE__ && GlobalVariables.enableAllConnectionParamForExternalActivities ? stringResources.HdiOnDemand_DisplayName : stringResources.Hdi_DisplayName,
    fullName: stringResources.HdiOnDemand_FullName,
    disableQueryAPI: true,
    onlyAzureIR: true,
    icon: {
        className: 'svg-i-100x100-azure_hdinsight'
    },
    supportedInTrident: true,
    supportAutoFillCopilotInput: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'AzureHDInsightOnDemandCluster' as ResourceKind
    },
    isCompute: true,
    isFirstClass: true,
    category: ['Compute'],
    copySinkTypes: [],
    copySourceTypes: [],
    controls: [
        {
            type: 'select-switch',
            label: stringResources.ModelName_HdiOnDemand_ClusterType,
            description: stringResources.HdiOnDemand_ToolTip_clusterType,
            options: [
                {
                    label: 'hadoop',
                    value: 'hadoop'
                },
                {
                    label: 'spark',
                    value: 'spark'
                }
            ],
            cases: [
                {
                    caseValue: 'hadoop',
                    children: []
                },
                {
                    caseValue: 'spark',
                    children: [
                        {
                            type: 'textbox',
                            bind: 'sparkVersion',
                            label: stringResources.ModelName_SparkVersion,
                            isLabelVertical: true
                        }
                    ]
                }
            ],
            bind: 'clusterType',
            defaultValue: 'hadoop',
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'clusterSize',
            description: stringResources.HdiOnDemand_ToolTip_clusterSize,
            label: stringResources.ModelName_HdiOnDemand_ClusterSize,
            required: true,
            defaultValue: '4',
            validators: [{ type: 'required', errorMessage: '' }, { type: 'number' }],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'timeToLive',
            description: stringResources.HdiOnDemand_ToolTip_timetolive,
            label: stringResources.ModelName_HdiOnDemand_TimeToLive,
            required: true,
            defaultValue: '00:15:00',
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'clusterNamePrefix',
            description: stringResources.HdiOnDemand_ToolTip_clusterNamePrefix,
            label: stringResources.ModelName_clusterNamePrefix,
            isLabelVertical: true
        },
        resourceGroupSelection,
        managedIdentityServicePrincipalDropdown,
        {
            type: 'select-switch',
            bind: 'version',
            description: stringResources.HdiOnDemand_ToolTip_version,
            label: stringResources.VersionLabel,
            hideDropdownWhenOptionsLessThan2: true,
            options: [
                {
                    label: '3.6',
                    value: '3.6',
                    hide: '{ isNotExistingversion }'
                },
                {
                    label: '4.0',
                    value: '4.0'
                }
            ],
            defaultValue: '4.0',
            isLabelVertical: true
        },
        {
            type: 'linkedservice-select-for-sidenav',
            bind: 'storageLinkedService',
            linkedServiceTypes: () => [...supportAzureBlobType, Metamodel.modelFor(AzureBlobFSLinkedService).name],
            label: stringResources.AzureStorage_LinkedServiceLabel,
            newLinkedServiceOptionText: stringResources.AddNew,
            required: true,
            description: stringResources.HdiOnDemand_ToolTip_linkedServiceName,
            validators: [{ type: 'required', errorMessage: '' }]
        },
        {
            type: 'accordion',
            header: stringResources.ModelName_AdditionalStorageProperties,
            required: true,
            children: [
                {
                    type: 'linkedservice-multiselect-for-sidenav',
                    bind: 'additionalLinkedServices',
                    description: formatStringForAppType(
                        stringResources.HdiOnDemand_ToolTip_additionalLinkedServiceNames
                    ),
                    linkedServiceTypes: supportAzureBlobType,
                    label: stringResources.ModelName_HdiOnDemand_AdditionalLinkedServices
                },
                {
                    type: 'linkedservice-select-for-sidenav',
                    bind: 'hcatalogLinkedService',
                    description: stringResources.HdiOnDemand_ToolTip_hcatalogLinkedServiceName,
                    linkedServiceTypes: ['AzureSqlDatabase'],
                    label: stringResources.ModelName_HdiOnDemand_HCatalogLinkedService,
                    newLinkedServiceOptionText: stringResources.AddNew
                }
            ]
        },
        {
            type: 'accordion',
            header: stringResources.ModelName_OsType,
            children: [
                {
                    type: 'textbox',
                    label: stringResources.ModelName_OsType,
                    description: stringResources.HdiOnDemand_ToolTip_osType,
                    bind: 'osType',
                    isLabelVertical: true,
                    defaultValue: 'Linux',
                    disabled: true
                },
                {
                    type: 'textbox',
                    bind: 'clusterSshUserName',
                    label: stringResources.ModelName_clusterSshUserName,
                    description: stringResources.HdiOnDemand_ToolTip_clusterSshUserName,
                    isLabelVertical: true
                },
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'clusterSshPassword',
                    label: stringResources.ModelName_clusterSshPassword,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_clusterSshPassword,
                        description: stringResources.HdiOnDemand_ToolTip_clusterSshPassword,
                        isLabelVertical: true
                    },
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        stringResources.HdiOnDemand_FullName,
                        stringResources.ModelName_clusterSshPassword
                    )
                },
                {
                    type: 'textbox',
                    bind: 'clusterUserName',
                    label: stringResources.ModelName_clusterUserName,
                    description: stringResources.HdiOnDemand_ToolTip_clusterUserName,
                    isLabelVertical: true
                },
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'clusterPassword',
                    label: stringResources.ModelName_clusterPassword,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_clusterPassword,
                        description: stringResources.HdiOnDemand_ToolTip_clusterPassword,
                        isLabelVertical: true
                    },
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        stringResources.HdiOnDemand_FullName,
                        stringResources.ModelName_clusterPassword
                    )
                }
            ]
        },
        {
            type: 'accordion',
            header: stringResources.ModelName_ScriptAction,
            description: stringResources.HdiOnDemand_ToolTip_multiScriptActionSupportComingSoon,
            children: [
                {
                    type: 'textbox',
                    label: stringResources.ModelName_ScriptName,
                    description: stringResources.HdiOnDemand_ToolTip_scriptName,
                    descriptionLink: stringResources.HdiOnDemand_ToolTipLink_scriptAction,
                    bind: 'scriptName',
                    isLabelVertical: true
                },
                {
                    type: 'textbox',
                    bind: 'scriptUri',
                    label: stringResources.ModelName_ScriptUri,
                    description: stringResources.HdiOnDemand_ToolTip_scriptUri,
                    descriptionLink: stringResources.HdiOnDemand_ToolTipLink_scriptAction,
                    isLabelVertical: true
                },
                {
                    type: 'multi-checkbox',
                    bind: 'scriptNodeTypes',
                    label: stringResources.ModelName_ScriptNodeTypes,
                    description: stringResources.HdiOnDemand_ToolTip_scriptNodeTypes,
                    options: [
                        {
                            label: stringResources.HdiOnDemand_HeadNode_DisplayName,
                            value: 'headnode'
                        },
                        {
                            label: stringResources.WorkerLabel,
                            value: 'workernode'
                        },
                        {
                            label: stringResources.HdiOnDemand_ZookeeperNode_DisplayName,
                            value: 'zookeeper'
                        }
                    ],
                    isLabelVertical: true
                },
                {
                    type: 'textarea',
                    bind: 'scriptParameters',
                    label: stringResources.ModelName_ScriptParameters,
                    description: stringResources.HdiOnDemand_ToolTip_scriptParameters,
                    isLabelVertical: true,
                    rows: 2
                }
            ]
        },
        {
            type: 'accordion',
            header: stringResources.AdvancedProperties,
            children: [
                {
                    type: 'textbox',
                    bind: 'headNodeSize',
                    label: stringResources.ModelName_HeadNodeSize,
                    isLabelVertical: true
                },
                {
                    type: 'textbox',
                    bind: 'dataNodeSize',
                    label: stringResources.ModelName_DataNodeSize,
                    isLabelVertical: true
                },
                {
                    type: 'textbox',
                    bind: 'zookeeperNodeSize',
                    label: stringResources.ModelName_ZookeeperNodeSize,
                    isLabelVertical: true
                },
                {
                    type: 'parameter-config',
                    bind: 'coreConfiguration',
                    label: stringResources.ModelName_CoreConfig,
                    description: stringResources.HdiOnDemand_ToolTip_coreConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'hbaseConfiguration',
                    label: stringResources.ModelName_HBaseConfig,
                    description: stringResources.HdiOnDemand_ToolTip_hBaseConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'hdfsConfiguration',
                    label: stringResources.ModelName_HDFSConfig,
                    description: stringResources.HdiOnDemand_ToolTip_hdfsConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'hiveConfiguration',
                    label: stringResources.ModelName_HiveConfig,
                    description: stringResources.HdiOnDemand_ToolTip_hiveConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'mapReduceConfiguration',
                    label: stringResources.ModelName_MapReduceConfig,
                    description: stringResources.HdiOnDemand_ToolTip_mapReduceConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'oozieConfiguration',
                    label: stringResources.ModelName_OozieConfig,
                    description: stringResources.HdiOnDemand_ToolTip_oozieConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'stormConfiguration',
                    label: stringResources.ModelName_StormConfig,
                    description: stringResources.HdiOnDemand_ToolTip_stormConfiguration
                },
                {
                    type: 'parameter-config',
                    bind: 'yarnConfiguration',
                    label: stringResources.ModelName_YarnConfig,
                    description: stringResources.HdiOnDemand_ToolTip_yarnConfiguration
                }
            ]
        }
    ]
};
